import { ChartTooltip } from "@/components/ui/chart-tooltip";
import { formatNumber } from "@/lib/format";
import type { ChartBlock, ChartPoint, ChartSeries } from "@/types/blocks";

// Fixed viewBox scaled to container width (house pattern). Geometry `.toFixed()`
// is pixel math — exempt from the formatter gate; axis/tooltip LABELS use
// `@/lib/format`.
const W = 640;
const H = 260;
const PAD_L = 44;
const PAD_R = 16;
const PAD_TOP = 16;
const PAD_BOTTOM = 40;
const PLOT_W = W - PAD_L - PAD_R;
const PLOT_H = H - PAD_TOP - PAD_BOTTOM;

const SERIES_TOKENS = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
];
const seriesColor = (i: number) => SERIES_TOKENS[i % SERIES_TOKENS.length];
const AXIS = "var(--muted-foreground)";
const GRID = "var(--border)";

const pointLabel = (p: ChartPoint) => p.formatted ?? formatNumber(p.y);

function maxY(series: ChartSeries[]): number {
  const m = Math.max(0, ...series.flatMap((s) => s.points.map((p) => p.y)));
  return m === 0 ? 1 : m;
}

/** A Maxwell chart. Series colors come from `--chart-1..5` (never hardcoded
 *  hex); hover detail uses the shared `<ChartTooltip>`. */
export function ChartBlockView({ block }: { block: ChartBlock }) {
  return (
    <figure className="rounded-md border border-border bg-card p-3">
      {block.title && (
        <figcaption className="mb-2 text-sm font-medium text-foreground">{block.title}</figcaption>
      )}
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label={block.title ?? "chart"}>
        <ChartBody block={block} />
      </svg>
    </figure>
  );
}

function ChartBody({ block }: { block: ChartBlock }) {
  switch (block.kind) {
    case "pie":
      return <PieChart block={block} />;
    case "funnel":
      return <FunnelChart block={block} />;
    case "line":
    case "area":
    case "scatter":
      return <XYChart block={block} />;
    case "bar":
    default:
      return <BarChart block={block} />;
  }
}

function YAxis({ max }: { max: number }) {
  const ticks = [0, 0.5, 1].map((f) => f * max);
  return (
    <>
      {ticks.map((t, i) => {
        const y = PAD_TOP + PLOT_H - (t / max) * PLOT_H;
        return (
          <g key={i}>
            <line x1={PAD_L} y1={y} x2={W - PAD_R} y2={y} stroke={GRID} strokeWidth={0.5} />
            <text x={PAD_L - 6} y={y + 3} textAnchor="end" fontSize={9} fill={AXIS}>
              {formatNumber(t)}
            </text>
          </g>
        );
      })}
    </>
  );
}

function BarChart({ block }: { block: ChartBlock }) {
  const points = block.series[0]?.points ?? [];
  const max = maxY(block.series);
  const slot = PLOT_W / Math.max(points.length, 1);
  const bw = slot * 0.6;
  return (
    <>
      <YAxis max={max} />
      {points.map((p, i) => {
        const h = (p.y / max) * PLOT_H;
        const x = PAD_L + i * slot + (slot - bw) / 2;
        const y = PAD_TOP + PLOT_H - h;
        return (
          <g key={i}>
            <rect data-bar x={x} y={y} width={bw} height={Math.max(h, 0)} fill={seriesColor(0)} rx={2} />
            <ChartTooltip
              trigger={<rect x={x} y={PAD_TOP} width={bw} height={PLOT_H} fill="transparent" />}
              title={p.x}
              rows={[{ label: block.series[0]?.name ?? "", value: pointLabel(p), dot: seriesColor(0) }]}
            />
            <text x={x + bw / 2} y={H - PAD_BOTTOM + 14} textAnchor="middle" fontSize={9} fill={AXIS}>
              {p.x}
            </text>
          </g>
        );
      })}
    </>
  );
}

function XYChart({ block }: { block: ChartBlock }) {
  const max = maxY(block.series);
  const n = Math.max(block.x_labels.length, 1);
  const xOf = (i: number) => PAD_L + (n === 1 ? PLOT_W / 2 : (i / (n - 1)) * PLOT_W);
  const yOf = (v: number) => PAD_TOP + PLOT_H - (v / max) * PLOT_H;
  return (
    <>
      <YAxis max={max} />
      {block.series.map((s, si) => {
        const color = seriesColor(si);
        const d = s.points.map((p, i) => `${i === 0 ? "M" : "L"} ${xOf(i).toFixed(1)} ${yOf(p.y).toFixed(1)}`).join(" ");
        return (
          <g key={si}>
            {block.kind === "area" && (
              <path
                d={`${d} L ${xOf(s.points.length - 1).toFixed(1)} ${(PAD_TOP + PLOT_H).toFixed(1)} L ${xOf(0).toFixed(1)} ${(PAD_TOP + PLOT_H).toFixed(1)} Z`}
                fill={color}
                opacity={0.15}
              />
            )}
            {block.kind !== "scatter" && <path d={d} fill="none" stroke={color} strokeWidth={1.5} />}
            {s.points.map((p, i) => (
              <ChartTooltip
                key={i}
                trigger={<circle data-point cx={xOf(i)} cy={yOf(p.y)} r={4} fill={color} />}
                title={p.x}
                rows={[{ label: s.name, value: pointLabel(p), dot: color }]}
              />
            ))}
          </g>
        );
      })}
      {block.x_labels.map((label, i) => (
        <text key={i} x={xOf(i)} y={H - PAD_BOTTOM + 14} textAnchor="middle" fontSize={9} fill={AXIS}>
          {label}
        </text>
      ))}
    </>
  );
}

/** Cumulative start/end angle per slice, starting at 12 o'clock. Pure — kept
 *  outside the component so no render-scope mutation is involved. */
function pieSliceAngles(points: ChartPoint[]): { start: number; end: number }[] {
  const total = points.reduce((sum, p) => sum + p.y, 0) || 1;
  const out: { start: number; end: number }[] = [];
  let angle = -Math.PI / 2;
  for (const p of points) {
    const end = angle + (p.y / total) * 2 * Math.PI;
    out.push({ start: angle, end });
    angle = end;
  }
  return out;
}

function PieChart({ block }: { block: ChartBlock }) {
  const points = block.series[0]?.points ?? [];
  const angles = pieSliceAngles(points);
  const cx = W / 2;
  const cy = H / 2;
  const r = Math.min(PLOT_H, PLOT_W) / 2 - 8;
  return (
    <>
      {points.map((p, i) => {
        const { start, end } = angles[i];
        const large = end - start > Math.PI ? 1 : 0;
        const x1 = cx + r * Math.cos(start);
        const y1 = cy + r * Math.sin(start);
        const x2 = cx + r * Math.cos(end);
        const y2 = cy + r * Math.sin(end);
        const d = `M ${cx} ${cy} L ${x1.toFixed(1)} ${y1.toFixed(1)} A ${r} ${r} 0 ${large} 1 ${x2.toFixed(1)} ${y2.toFixed(1)} Z`;
        return (
          <ChartTooltip
            key={i}
            trigger={<path data-slice d={d} fill={seriesColor(i)} />}
            title={p.x}
            rows={[{ label: block.series[0]?.name ?? "", value: pointLabel(p), dot: seriesColor(i) }]}
          />
        );
      })}
    </>
  );
}

function FunnelChart({ block }: { block: ChartBlock }) {
  const points = block.series[0]?.points ?? [];
  const max = maxY(block.series);
  const rowH = PLOT_H / Math.max(points.length, 1);
  return (
    <>
      {points.map((p, i) => {
        const w = (p.y / max) * PLOT_W;
        const x = PAD_L + (PLOT_W - w) / 2;
        const y = PAD_TOP + i * rowH + 2;
        return (
          <g key={i}>
            <ChartTooltip
              trigger={<rect data-funnel x={x} y={y} width={Math.max(w, 1)} height={rowH - 4} fill={seriesColor(i)} rx={2} />}
              title={p.x}
              rows={[{ label: block.series[0]?.name ?? "", value: pointLabel(p), dot: seriesColor(i) }]}
            />
            <text x={PAD_L} y={y + rowH / 2} fontSize={9} fill={AXIS} dominantBaseline="middle">
              {p.x}
            </text>
          </g>
        );
      })}
    </>
  );
}
